import type { workflowEditorFr } from './workflow-editor.fr';

type DeepStrings<T> = { [K in keyof T]: T[K] extends string ? string : DeepStrings<T[K]> };

/** English texts of the workflow graph editor, parameterised launch and run page (ADR 0102). */
export const workflowEditorEn: DeepStrings<typeof workflowEditorFr> = {
  newTitle: 'New workflow',
  editTitle: 'Edit {{name}}',
  subtitle: 'Draw the steps and link them: a step starts when the steps it depends on are done.',
  openEditor: 'Graph editor',
  openWizard: 'Step-by-step wizard',
  editGraph: 'Edit graph',
  tabs: { graph: 'Graph', json: 'JSON' },
  actions: {
    autoLayout: 'Auto layout',
    save: 'Save',
    create: 'Create workflow',
    cancel: 'Cancel',
    deleteStep: 'Delete step',
    settings: 'Workflow settings',
  },
  palette: {
    title: 'Steps',
    hint: 'Click to add, then link the handles.',
    groups: { run: 'Run', flow: 'Control flow', call: 'Call a service' },
  },
  types: {
    job: 'Job',
    script: 'Script',
    workflow: 'Sub-workflow',
    condition: 'Condition',
    time: 'Wait',
    error: 'Error',
    http: 'HTTP call',
    agent: 'AI agent',
    inference: 'Inference',
    catalog: 'Catalog',
    wait: 'Sensor',
    approval: 'Approval',
  },
  typeUnknown: 'Type “{{type}}”',
  typeHelp: {
    job: 'Runs a job of the project and waits for it.',
    script: 'Runs a bash script in a pod; its values arrive in GRAAL_WORKFLOW_PARAMETERS.',
    workflow: 'Runs another workflow of the project and waits for it (5 levels at most, no cycle).',
    condition: 'Evaluates an expression: when false, the step and everything after it are skipped.',
    time: 'Waits for a duration or until a date, without a pod.',
    error: 'Fails the run as soon as it is reached.',
    http: 'Calls an API through an http connection of the project, or a public URL.',
    agent: 'Asks a model of the gateway, a graal Agentic Platform work item or an A2A agent.',
    inference: 'Calls a serving endpoint, the LLM gateway, an OpenAI-compatible or a KServe V2 server.',
    catalog: 'Acts on OpenMetadata: ingestion, entity lookup, data quality, lineage.',
    wait: 'Waits until an object arrives, an API answers or another run succeeds.',
    approval: 'Waits for a person to decide: approve or reject.',
  },
  canvas: {
    label: 'Workflow graph',
    empty: 'No step yet: add one from the palette.',
    forEach: 'fan-out',
    lock: 'lock {{lock}}',
    problems_one: '{{count}} problem',
    problems_other: '{{count}} problems',
  },
  panel: {
    step: 'Step',
    typeSection: 'Parameters',
    commonSection: 'Execution',
    targetSection: 'Target',
    unknownType:
      'Type unknown to the console: its fields are kept as they are when saving. Edit them in the JSON tab.',
    depends: 'Depends on',
    noDepends: 'No upstream step: starts the workflow.',
  },
  references: {
    title: 'Insert a reference',
    help: 'Click a reference: it is appended to the last text field you chose, or copied.',
    upstream: 'Outputs of an upstream step',
    noUpstream: 'No upstream step: link one to cite its outputs.',
    params: 'Run parameters',
    item: 'Current item (fan-out)',
    run: 'The run',
    inserted: 'Reference appended to “{{field}}”.',
    copied: 'Reference copied.',
    syntax:
      'Templates: an expression between double braces. Filters: json, default, urlencode. A cited step must be upstream.',
  },
  common: {
    name: 'Step name',
    description: 'Description',
    retries: 'Retries after failure',
    retry_delay_seconds: 'Delay before retry (s)',
    timeout_seconds: 'Maximum duration (s)',
    trigger_rule: 'Trigger rule',
    for_each: 'Fan-out (for_each)',
    max_parallel: 'Items in parallel',
    lock: 'Named lock',
    infrastructure_id: 'Infrastructure',
    device_id: 'Device',
  },
  commonHelp: {
    name: 'Letters, digits, - and _; unique in the workflow. Steps depending on it follow a rename.',
    retries: '0 to 10 new attempts.',
    timeout_seconds: 'Beyond it the attempt fails (then retries apply).',
    trigger_rule: 'All succeeded (default), all done whatever the outcome, or at least one failed.',
    for_each: 'A template that renders a JSON list, or a literal list: one attempt per item (1,000 at most).',
    max_parallel: '1 to 64, 4 by default.',
    lock: 'Two steps holding the same lock never run at the same time, across the project.',
    target: 'Replaces the target of the run for this step (one OR the other).',
  },
  triggerRules: {
    all_success: 'All succeeded',
    all_done: 'All done',
    one_failed: 'At least one failed',
  },
  fields: {
    ref: 'Job',
    workflowRef: 'Workflow',
    parameters: 'Parameters (name=value)',
    script: 'Script',
    expression: 'Expression',
    duration_seconds: 'Duration (seconds)',
    until: 'Until (ISO 8601 date)',
    message: 'Message',
    connection_id: 'http connection',
    url: 'Public URL',
    method: 'Method',
    path: 'Path',
    query: 'Query parameters (name=value)',
    headers: 'Headers (name=value)',
    body: 'Body',
    content_type: 'Body content type',
    expected_status: 'Expected statuses',
    success_condition: 'Success condition',
    provider: 'Provider',
    prompt: 'Instruction',
    system: 'System prompt',
    model: 'Model',
    rag_index_id: 'Document index (RAG)',
    output_format: 'Answer format',
    temperature: 'Temperature',
    max_tokens: 'Max tokens',
    agent_project: 'Agentic Platform project',
    agent_workflow: 'Agentic Platform workflow',
    title: 'Work item title',
    fields: 'Work item fields (name=value)',
    labels: 'Labels',
    budget_usd: 'Budget (USD)',
    accept_outcomes: 'Accepted outcomes',
    poll_interval_seconds: 'Polling interval (s)',
    endpoint_id: 'Endpoint',
    operation: 'Operation',
    input: 'Input (JSON)',
    action: 'Action',
    pipeline: 'Ingestion pipeline (FQN)',
    wait: 'Wait for the end',
    entity: 'Entity (FQN)',
    entity_type: 'Entity type',
    max_failures: 'Tolerated failures',
    upstream: 'Upstream entities (FQN)',
    sensor: 'Sensor',
    condition: 'Condition',
    bucket_id: 'Bucket',
    prefix: 'Prefix',
    min_count: 'At least (objects)',
    job_id: 'Watched job',
    workflow_id: 'Watched workflow',
    since: 'Since',
    poke_interval_seconds: 'Check interval (s)',
    soft_fail: 'Skip instead of failing at the timeout',
    approvers: 'Approvers (ids)',
    on_timeout: 'At the timeout',
    forbid_self_approval: 'Four eyes: the run launcher does not decide',
  },
  help: {
    parameters: 'One line per parameter; values accept templates. Never a secret.',
    expression:
      'Closed grammar: steps.<step>.status, steps.<step>.outputs.<key>, params.<name>, comparisons, ! && || and parentheses.',
    duration_seconds: 'Exactly one duration OR one date.',
    connection_id:
      'Base URL, secret and allowed addresses come from the connection. Exactly one connection OR one URL.',
    url: 'A public address only (private addresses are refused).',
    headers: 'Authorization, Cookie, Host and Proxy-* are refused: the identity comes from the connection.',
    expected_status: 'Default: any 2xx.',
    success_condition: 'Reads response.status, response.body… and response.headers["name"].',
    prompt: 'Accepts templates: cite the outputs of upstream steps.',
    rag_index_id: 'The passages retrieved for the instruction are given to the model.',
    output_format: 'json: the answer must be a JSON object, otherwise the attempt fails.',
    accept_outcomes: 'success (default), declined, cancelled.',
    input: 'A template that renders a JSON value: request body, inputs or messages.',
    max_failures: 'Beyond it, the step fails.',
    condition: 'Default: a 2xx status.',
    since: 'run_start (default), logical_date, or an ISO 8601 duration (P1D).',
    approvers: 'Empty: anyone who can run the workflow.',
    soft_fail: 'At the timeout, the step is skipped instead of failing.',
    forbid_self_approval:
      'The person who launched the run can neither approve nor reject this step: someone else must decide.',
  },
  enums: {
    llm: 'LLM gateway',
    agentic_platform: 'graal Agentic Platform',
    a2a: 'A2A agent',
    endpoint: 'Serving endpoint',
    openai: 'OpenAI-compatible',
    kserve_v2: 'KServe V2 (Triton, MLServer)',
    openmetadata: 'OpenMetadata',
    text: 'Text',
    json: 'JSON',
    chat: 'Chat',
    embeddings: 'Embeddings',
    completions: 'Completion',
    run_ingestion: 'Run an ingestion',
    get_entity: 'Read an entity',
    check_quality: 'Check data quality',
    add_lineage: 'Add lineage',
    http: 'An API answers',
    object: 'An object arrives',
    run: 'A run succeeds',
    fail: 'Fail',
    approve: 'Approve',
    skip: 'Skip the step',
  },
  options: { none: '— none —' },
  settings: {
    title: 'Workflow settings',
    help: 'Click a step to edit it; click the background to come back here.',
    name: 'Workflow name',
    description: 'Description',
    maxParallelTasks: 'Steps in parallel per run',
    maxParallelTasksHelp: '1 to 256; each fan-out item counts. Empty: no ceiling.',
    maxActiveRuns: 'Active runs at most',
    maxActiveRunsHelp: 'Beyond it, a manual launch is refused and a cron tick is skipped.',
    inputs: 'Declared parameters',
    inputsHelp: 'Checked at each launch; the “Run” form is generated from them.',
    addInput: 'Add a parameter',
    removeInput: 'Remove this parameter',
    input: 'Parameter {{position}}',
    schedule: 'Schedule',
    scheduleHelp: 'Manual, or scheduled by a cron expression; the same fields as the workflow summary.',
  },
  notifications: {
    title: 'Notifications',
    help: 'On start, success, failure, and the SLA alerts. A new webhook gets a signing secret, shown once on save.',
    empty: 'No notification.',
    add: 'Add a notification',
    addTitle: 'New notification',
    addDescription: 'It goes with the definition, when the workflow is saved.',
    remove: 'Remove this notification',
    events: {
      on_start: 'On start',
      on_success: 'On success',
      on_failure: 'On failure',
      on_alert: 'SLA alert',
    },
  },
  sla: {
    title: 'Service level (SLA)',
    help: 'Late, slow, failed then recovered alerts, sent to the “SLA alert” notifications. Empty: no threshold.',
    maxAge: 'Late after (s)',
    maxAgeHelp: 'Alert when the last success is older (1 s to 366 days).',
    slowFactor: 'Slow beyond p95 ×',
    slowFactorHelp: 'Alert when a run lasts longer than p95 times this factor (1 to 100, default 2).',
    timeoutFraction: 'Slow beyond this share of the timeout',
    timeoutFractionHelp:
      'Alert when a run exceeds this fraction of its timeout (above 0, at most 1; default 0.8).',
  },
  summary: {
    graph: 'Graph',
    graphLastRun: 'Colours of the last run, {{run}}',
    graphNoRun: 'No run yet',
    noSteps: 'This workflow has no step.',
    runs: 'Runs',
    successRate: 'succeeded',
    noRuns: 'Never run',
    counts: '{{total}} runs · {{failed}} failed · {{running}} running',
    lastRun: 'Last run',
    schedule: 'Schedule',
    manual: 'Manual or by trigger',
    infrastructure: 'Infrastructure',
    inputs: 'Parameters',
    noInputs: 'None',
    labels: 'Labels',
    identity: 'Run identity',
    updated: 'Updated',
    description: 'Description',
    steps: 'Steps',
    settings: {
      title: 'Name, schedule and retries',
      hint: 'The graph, notifications and SLA are edited in the editor.',
      edit: 'Edit',
      close: 'Close',
    },
    cron: {
      everyMinute: 'Every minute',
      everyMinutes_one: 'Every minute',
      everyMinutes_other: 'Every {{count}} minutes',
      hourly: 'Every hour, at minute {{minute}}',
      everyHours_one: 'Every hour, at minute {{minute}}',
      everyHours_other: 'Every {{count}} hours, at minute {{minute}}',
      daily: 'Every day at {{time}}',
      weekly: '{{days}} at {{time}}',
      dayRange: '{{from}} to {{to}}',
      monthly: 'On day {{day}} of every month at {{time}}',
    },
  },
  history: {
    tab: 'History',
    title: 'Definition history',
    description:
      'Every creation and every change of the definition is a revision. Restoring one saves a new revision.',
    empty: 'No revision.',
    emptyHelp: 'Revisions appear when the workflow is saved.',
    columns: {
      revision: 'Revision',
      created: 'Saved',
      author: 'Author',
      name: 'Name',
      steps: 'Steps',
    },
    stepCount_one: '{{count}} step',
    stepCount_other: '{{count}} steps',
    current: 'current',
    view: 'View',
    viewLabel: 'View revision {{revision}}',
    revisionTitle: 'Revision {{revision}}',
    revisionMeta: 'Saved on {{date}} by {{author}}',
    graph: 'Revision graph',
    identical: 'Identical to the current definition.',
    changes_one: '{{count}} change to restore',
    changes_other: '{{count}} changes to restore',
    restore: 'Restore',
    confirmTitle: 'Restore revision {{revision}}?',
    confirmDescription_one:
      'The current definition is replaced by this one ({{count}} change). History keeps it: a new revision is written.',
    confirmDescription_other:
      'The current definition is replaced by this one ({{count}} changes). History keeps it: a new revision is written.',
    restored: 'Revision {{revision}} restored.',
    restoreFailed: 'The restore failed.',
  },
  picker: {
    title: 'In which project?',
    help: 'A workflow lives in a project: choose the one to open the editor in.',
  },
  inputs: {
    name: 'Name',
    type: 'Type',
    default: 'Default value',
    required: 'Required',
    enum: 'Allowed values',
    description: 'Description',
    types: {
      string: 'Text',
      number: 'Number',
      boolean: 'Boolean',
      json: 'JSON',
      date: 'Date',
    },
  },
  json: {
    label: 'Definition (JSON)',
    help: 'The definition sent to the API. Apply to go back to the graph; unknown fields are kept.',
    apply: 'Apply to the graph',
    applied: 'Definition applied to the graph.',
    invalidSyntax: 'Unreadable JSON: {{detail}}',
    invalidShape: 'An object is expected, and “tasks” must be a list of objects.',
  },
  validation: {
    title: 'Validation',
    checking: 'Checking with the server…',
    valid: 'Valid definition',
    local_one: '{{count}} problem to fix',
    local_other: '{{count}} problems to fix',
    server: 'Refused by the server',
    serverUnavailable: 'Server validation unavailable: only the local check applies.',
    levels_one: '{{count}} execution level',
    levels_other: '{{count}} execution levels',
    workflow: 'Workflow',
    blocked: 'Fix the reported problems before saving.',
  },
  problems: {
    noSteps: 'The workflow has no step.',
    nameRequired: 'The name is required.',
    nameInvalid: 'Invalid name: letters, digits, - and _, 63 characters at most.',
    nameDuplicate: 'Two steps have this name.',
    dependencyUnknown: 'Depends on “{{step}}”, which does not exist.',
    dependencySelf: 'A step cannot depend on itself.',
    cycle: 'The step is in a cycle (or after one).',
    fieldRequired: 'Required field: {{field}}.',
    exactlyOne: 'Exactly one of “{{a}}” or “{{b}}”.',
    atMostOne: 'At most one of “{{a}}” or “{{b}}”.',
    tooLong: '{{field}}: {{max}} characters at most.',
    range: '{{field}}: between {{min}} and {{max}}.',
    notInteger: '{{field}}: a whole number is expected.',
    invalidInstant: 'An ISO 8601 date with a time zone is expected (2026-11-04T06:00:00Z).',
    invalidLock: 'Invalid lock: letters, digits, _ . : -, 128 characters at most.',
    invalidStatus: 'HTTP statuses between 100 and 599.',
    invalidEnum: '{{field}}: value not allowed.',
    forbiddenHeader: 'Header “{{header}}” refused: the identity comes from the connection.',
    forEachNotAllowed: 'This step type does not accept a fan-out.',
    templateUnclosed: '{{field}}: template not closed.',
    templateNotUpstream: '{{field}}: “{{step}}” is not an upstream step.',
    templateItemOutsideForEach: '{{field}}: “item” only exists in a fan-out step.',
    workflowNameRequired: 'The workflow name is required.',
    workflowNameInvalid: 'Invalid workflow name: letters, digits, - _ and ., 63 characters at most.',
    inputNameInvalid: 'Invalid parameter name (a letter or _, then letters, digits, _ . -).',
    inputDuplicate: 'Two parameters have this name.',
    inputDefaultNotAllowed: 'The default value is not an allowed value.',
    inputDefaultType: 'The default value is not of the declared type.',
    cronInvalid: 'Invalid cron expression.',
    slaFraction: '{{field}}: above 0, at most 1.',
  },
  saved: 'Workflow saved.',
  created: 'Workflow “{{name}}” created.',
  saveFailed: 'Saving failed.',
  unchanged: 'No change to save.',
  launch: {
    title: 'Run the workflow',
    description: 'Declared parameters are checked before sending; an empty value takes its default.',
    submit: 'Run',
    infrastructure: 'Infrastructure',
    defaultValue: 'default: {{value}}',
    notAllowed: 'Value not allowed.',
    invalid: {
      string: 'Invalid value.',
      number: 'A number is expected.',
      boolean: 'true or false.',
      json: 'Valid JSON is expected.',
      date: 'An ISO 8601 date is expected (2026-11-03).',
    },
    noInfrastructure: 'No usable infrastructure: a run cannot start.',
  },
  run: {
    graph: 'Run graph',
    graphHint: 'Colour = step status. Click a step for its attempts, outputs and progress.',
    select: 'Click a step of the graph.',
    attempts: 'Attempts',
    attempt: 'Attempt {{number}}',
    outputs: 'Outputs',
    noOutputs: 'No output.',
    progress: 'Progress',
    error: 'Error',
    items_one: '{{count}} item',
    items_other: '{{count}} items',
    item: 'Item {{index}}',
    notStarted: 'Not started yet.',
    openChildRun: 'Open the run',
    approvals: {
      title: 'Pending approvals',
      waiting: 'Step “{{step}}” is waiting for a decision.',
      approve: 'Approve',
      reject: 'Reject',
      comment: 'Comment',
      commentHelp: 'Kept in the step outputs; it is the failure reason of a rejection.',
      approveTitle: 'Approve “{{step}}”',
      rejectTitle: 'Reject “{{step}}”',
      dialogDescription: 'The decision is final: the run resumes (approved) or the step fails (rejected).',
      approved: 'Step “{{step}}” approved.',
      rejected: 'Step “{{step}}” rejected.',
      failed: 'The decision was not recorded.',
      notWaiting: 'This step is no longer waiting for a decision.',
      forbidden:
        'Decision refused: you launched this run and the step requires four eyes, or you are not among the approvers.',
    },
  },
};
